import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { hasUnseenChanges, watchUnseenChanges } from '../../../../services/Changelog';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';

/** The lobby's "What's new" link, top left, to the changelog page. A dot on it
 *  marks changes the player hasn't opened yet; the page is the list itself, so
 *  nothing is fetched here. The page records a viewing as it loads, whichever
 *  link opened it, and the dot follows through the service's watch. */
@customElement('changelog-link')
export class ChangelogLink extends LitElement {
  static override styles = [
    tokens,
    css`
      /* Placed by airmash-lobby's top row. Mirrors login-status on the
         right, the link styled like its Logout. */
      :host {
        display: block;
        position: relative;
        pointer-events: auto;
        font-family: var(--font-family);
        /* Legacy's 12px, as the rest of the row. */
        font-size: var(--font-size-sm);
        text-shadow: var(--shadow-text);
        color: var(--color-text-tooltip);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      .trigger {
        position: relative;
        display: flex;
        align-items: center;
        gap: 7px;
        font: inherit;
        color: inherit;
        text-shadow: inherit;
        font-weight: bold;
        text-decoration: none;
        border-radius: var(--radius-md);
        padding: 10px 12px;
        cursor: pointer;
      }

      @media (hover: hover) {
        .trigger:hover {
          color: var(--color-text-white);
          background-color: var(--color-bg-chip);
        }
      }

      .trigger:active {
        color: var(--color-text-white);
        background-color: var(--color-bg-chip);
      }

      /* An invisible 44px tap target around the 35px link. */
      @media (hover: none) {
        .trigger::after {
          content: '';
          position: absolute;
          inset: -5px 0;
        }
      }

      .dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background-color: var(--color-accent-gold);
        box-shadow: 0 0 6px var(--color-accent-gold);
      }
    `,
  ];

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  @state() private unseen = hasUnseenChanges();

  private stopWatching?: () => void;

  override connectedCallback() {
    super.connectedCallback();
    this.stopWatching = watchUnseenChanges((unseen) => {
      this.unseen = unseen;
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopWatching?.();
    this.stopWatching = undefined;
  }

  /** The dot goes at once; the page records the viewing as it loads. */
  private handleClick() {
    this.api.playClick();
    this.unseen = false;
  }

  override render() {
    return html`<a class="trigger" href="/changelog.html" target="_blank" rel="noopener" @click=${this.handleClick}>
      What's new${this.unseen ? html`<span class="dot" aria-hidden="true"></span>` : nothing}
    </a>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'changelog-link': ChangelogLink;
  }
}
